import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FiChevronsDown, FiChevronsUp } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { useBookingContext } from '../../../contexts/BookingProvider';
import { AvailableAssociate } from '../../../types/appointments';
import { Client } from '../../../types/users';
import { showFullName } from '../../../utils/commonFunctions';
import BookingAvailabilityRow from './BookingAvailabilityRow';

const BookingSuggestionRow = ({
  date,
  associates,
}: {
  date: string;
  associates: Array<AvailableAssociate>;
}) => {
  // integration of react hooks here
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [direction, setDirection] = useState<-1 | 1 | 0>(0);
  const [animatePosition, setAnimatePosition] = useState<number>(0);
  const [initialPosition, setInitialPosition] = useState<number>(0);

  // integration of context hooks here
  const {
    selectedDate,
    setSelectedAssociate,
    setShowAvailableSlotModal,
    setShowUpdateClientModal,
    setSearchDate,
  } = useBookingContext();
  const { currentUserType, currentUser } = useAuthContext();

  const formattedDate = moment(date).format('DD-MM-YYYY');

  const totalPages = Math.ceil(associates.length / 3);

  console.log('CURRENT: ', animatePosition);
  console.log('INITIAL: ', initialPosition);

  // setting initial position and animation position here on clicking buttons
  useEffect(() => {
    if (direction === -1) {
      setInitialPosition(animatePosition);
      setAnimatePosition((state) => state - 135);
      setDirection(0);
    } else if (direction === 1) {
      setInitialPosition(animatePosition);
      setAnimatePosition((state) => state + 135);
      setDirection(0);
    }
  }, [direction, animatePosition]);

  // rendering booking suggestion row component here
  return (
    <div
      className={`grid grid-cols-[110px_1fr] [&:not(:last-child)]:border-b-2 border-legal-dark-blue border-dashed min-w-[1000px] ${
        formattedDate === selectedDate && 'bg-base-light-green/30'
      }`}
    >
      <div className='flex flex-col items-center justify-center font-semibold text-base-cyan border-r-2 border-legal-dark-blue border-dashed'>
        <h4>{formattedDate}</h4>
        <p className='text-xs text-legal-dark-blue italic'>
          ({moment(date).format('dddd')})
        </p>
      </div>

      {associates.length ? (
        <div
          className={`group relative grid grid-cols-[200px_150px_1fr] [&:not(:last-child)]:border-b-2 [&>div:not(:last-child)]:border-r-2 [&>div]:border-dashed [&>div]:border-legal-dark-blue overflow-hidden ${
            associates.length > 2 ? 'h-[134px]' : 'h-fit'
          }`}
        >
          {associates.length > 3 ? (
            <div className='absolute flex flex-col items-center justify-between left-[199px] -translate-x-1/2 h-full w-fit z-10 py-1 !border-r-0 opacity-0 group-hover:opacity-100 duration-300'>
              <button
                onClick={() => {
                  setCurrentPage((state) => state - 1);
                  setDirection(1);
                }}
                className='flex items-center justify-center bg-base-white text-base-dark-blue rounded-full border-2 border-base-light-blue size-5 shadow-academia-sm hover:bg-base-light-blue hover:text-base-white active:scale-95 duration-300 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:border-gray-400 disabled:hover:text-base-dark-blue'
                disabled={currentPage === 1}
              >
                <FiChevronsUp className='size-4' />
              </button>

              <button
                onClick={() => {
                  setCurrentPage((state) => state + 1);
                  setDirection(-1);
                }}
                className='flex items-center justify-center bg-base-white text-base-dark-blue rounded-full border-2 border-base-light-blue size-5 shadow-academia-sm hover:bg-base-light-blue hover:text-base-white active:scale-95 duration-300 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:border-gray-400 disabled:hover:text-base-dark-blue'
                disabled={currentPage === totalPages}
              >
                <FiChevronsDown className='size-4' />
              </button>
            </div>
          ) : null}

          <AnimatePresence mode='wait'>
            <motion.div
              key={currentPage}
              initial={{
                y: initialPosition,
              }}
              animate={{ y: animatePosition }}
              exit={{ y: animatePosition }}
              transition={{ duration: 0.2 }}
              className={`text-center [&>div]:py-2 [&>div]:border-legal-dark-blue [&>div]:border-dashed ${
                associates.length > 2
                  ? '[&>div]:border-b'
                  : '[&>div:not(:last-child)]:border-b'
              }`}
            >
              {associates.map((associate, index) => (
                <div key={index}>
                  <p
                    title={showFullName(
                      associate.first_name,
                      associate.middle_name,
                      associate.last_name
                    )}
                    onClick={() => {
                      if (
                        currentUserType === 'client' &&
                        !(currentUser as Client).is_profile_complete
                      ) {
                        toast.warn('Please, complete your profile first!');
                        setShowUpdateClientModal(true);

                        return;
                      }

                      setSelectedAssociate(associate);
                      setShowAvailableSlotModal(true);
                      setSearchDate(date);
                    }}
                    className='text-sm shadow-academia-sm bg-base-white rounded-md py-1 px-2 mx-2 text-base-light-blue font-semibold truncate cursor-pointer hover:opacity-70 active:scale-95 duration-300'
                  >
                    {showFullName(
                      associate.first_name,
                      associate.middle_name,
                      associate.last_name
                    )}
                  </p>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode='wait'>
            <motion.div
              key={currentPage}
              initial={{
                y: initialPosition,
              }}
              animate={{ y: animatePosition }}
              exit={{ y: animatePosition }}
              transition={{ duration: 0.2 }}
              className={`text-center [&>div]:py-2 [&>div]:border-legal-dark-blue [&>div]:border-dashed ${
                associates.length > 2
                  ? '[&>div]:border-b'
                  : '[&>div:not(:last-child)]:border-b'
              }`}
            >
              {associates.map((associate, index) => (
                <div key={index} className='flex items-center justify-center'>
                  <p className='text-sm text-base-dark-green py-1'>
                    {associate.credits_per_appointment} Credits
                    <span className='text-xs text-base-orange'>/Slot</span>
                  </p>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode='wait'>
            <motion.div
              key={currentPage}
              initial={{
                y: initialPosition,
              }}
              animate={{ y: animatePosition }}
              exit={{ y: animatePosition }}
              transition={{ duration: 0.2 }}
              className={`[&>div]:border-legal-dark-blue [&>div]:border-dashed text-sm ${
                associates.length > 2
                  ? '[&>div]:border-b'
                  : '[&>div:not(:last-child)]:border-b'
              }`}
            >
              {associates.map((associate) => (
                <BookingAvailabilityRow
                  key={associate.id}
                  associate={associate}
                  date={date}
                />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      ) : (
        <div className='flex items-center justify-center bg-base-red/30'>
          <p className='text-base-red font-medium'>
            No Associate Available On This Day!!
          </p>
        </div>
      )}
    </div>
  );
};

export default BookingSuggestionRow;
